
import { supabase } from './supabase';

export interface GlobalStats {
  totalUsers: number;
  totalLocks: number;
  totalLeads: number;
  totalViews: number;
}

export interface UserSummary {
  userId: string;
  email?: string; // Often not available directly via public tables unless joined, using distinct ID for now
  lastActive: number;
  lockCount: number;
  leadCount: number;
  status: 'active' | 'blocked';
}

export const adminService = {
  // Check if current user is admin
  isAdmin: (email: string | undefined) => {
    return email === 'mart.vion@gmail.com';
  },

  // Fetch global stats
  getGlobalStats: async (): Promise<GlobalStats> => {
    // Note: In a real production app with millions of rows, 
    // you would use Supabase "approximate count" or a dedicated stats table.
    // For MVP, fetching count is fine.
    
    const { count: locksCount } = await supabase.from('locks').select('*', { count: 'exact', head: true });
    const { count: leadsCount } = await supabase.from('leads').select('*', { count: 'exact', head: true });
    
    // Aggregate views sum
    const { data: viewsData } = await supabase.from('locks').select('views_count');
    const totalViews = viewsData?.reduce((acc, curr) => acc + (curr.views_count || 0), 0) || 0;

    // Try to get exact user count from profiles if available, otherwise fallback to lock creators
    let totalUsers = 0;
    const { count: profilesCount, error: profilesError } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
    
    if (!profilesError && profilesCount !== null) {
        totalUsers = profilesCount;
    } else {
        // Fallback: distinct user_id in locks
        const { data: usersData } = await supabase.from('locks').select('user_id');
        const uniqueUsers = new Set(usersData?.map(u => u.user_id));
        totalUsers = uniqueUsers.size;
    }

    return {
      totalUsers,
      totalLocks: locksCount || 0,
      totalLeads: leadsCount || 0,
      totalViews: totalViews
    };
  },

  // Get List of Users (Aggregated from Locks)
  getUsersList: async (): Promise<UserSummary[]> => {
    // Fetch all locks to aggregate data
    // Warning: Heavy query for client side if scaling, acceptable for Admin Dashboard MVP
    const { data: locks } = await supabase.from('locks').select('user_id, created_at, leads_count');

    if (!locks) return [];

    const userMap = new Map<string, UserSummary>();

    locks.forEach(lock => {
      const existing = userMap.get(lock.user_id) || {
        userId: lock.user_id,
        lastActive: 0,
        lockCount: 0,
        leadCount: 0,
        status: 'active'
      };

      existing.lockCount += 1;
      existing.leadCount += (lock.leads_count || 0);
      
      const lockDate = new Date(lock.created_at).getTime();
      if (lockDate > existing.lastActive) {
        existing.lastActive = lockDate;
      }

      userMap.set(lock.user_id, existing);
    });

    return Array.from(userMap.values()).sort((a, b) => b.lastActive - a.lastActive);
  },

  // Mock Block User (Requires backend function to truly block in Auth)
  toggleBlockUser: async (userId: string) => {
     // In a real app: await supabase.rpc('toggle_user_ban', { user_id: userId });
     console.log("Toggling block for", userId);
     return true;
  },

  // Mock Delete User Data
  deleteUserContent: async (userId: string) => {
      // This is dangerous, usually handled by cascading deletes in Postgres
      const { error } = await supabase.from('locks').delete().eq('user_id', userId);
      if (error) throw error;
      return true;
  }
};
